import type {TopicAttachment} from '../services/topicsApi'

export type AttachmentPreviewKind =
  | 'image'
  | 'pdf'
  | 'text'
  | 'spreadsheet'
  | 'video'
  | 'audio'
  | 'unsupported'
export type AttachmentMetadata = Pick<TopicAttachment, 'name' | 'size' | 'mimeType'>

export const attachmentKindLabels: Record<AttachmentPreviewKind, string> = {
  image: 'Imagem',
  pdf: 'PDF',
  text: 'Texto',
  spreadsheet: 'Planilha',
  video: 'Vídeo',
  audio: 'Áudio',
  unsupported: 'Arquivo',
}

const imageTypes: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  avif: 'image/avif',
  ico: 'image/x-icon',
}
const videoTypes: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  ogv: 'video/ogg',
}
const audioTypes: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  flac: 'audio/flac',
  aac: 'audio/aac',
}

function extension(name: string) {
  return name.toLowerCase().split('.').pop() || ''
}

export function getAttachmentPreviewKind(file: AttachmentMetadata): AttachmentPreviewKind {
  const ext = extension(file.name)
  const mime = (file.mimeType || '').toLowerCase().split(';')[0]
  if (
    ['html', 'htm', 'svg', 'svgz', 'xml'].includes(ext) ||
    ['text/html', 'application/xhtml+xml', 'image/svg+xml'].includes(mime)
  )
    return 'unsupported'
  if (imageTypes[ext] || Object.values(imageTypes).includes(mime)) return 'image'
  if (ext === 'pdf' || mime === 'application/pdf') return 'pdf'
  if (
    ['xlsx', 'xls', 'csv', 'tsv'].includes(ext) ||
    [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'text/tab-separated-values',
    ].includes(mime)
  )
    return 'spreadsheet'
  if (videoTypes[ext] || Object.values(videoTypes).includes(mime)) return 'video'
  if (audioTypes[ext] || Object.values(audioTypes).includes(mime)) return 'audio'
  if (
    ['txt', 'log', 'json', 'md'].includes(ext) ||
    ['text/plain', 'application/json', 'text/markdown'].includes(mime)
  )
    return 'text'
  return 'unsupported'
}

export function formatAttachmentSize(bytes: number) {
  return bytes >= 1024 * 1024
    ? (bytes / (1024 * 1024)).toLocaleString('pt-BR', {maximumFractionDigits: 1}) + ' MB'
    : Math.max(1, Math.ceil(bytes / 1024)) + ' KB'
}

export function readAttachmentBuffer(blob: Blob, signal?: AbortSignal): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    const cancel = () => {
      reader.abort()
      reject(new DOMException('Leitura cancelada.', 'AbortError'))
    }
    const finish = () => signal?.removeEventListener('abort', cancel)
    if (signal?.aborted) {
      cancel()
      return
    }
    reader.onload = () => {
      finish()
      resolve(reader.result as ArrayBuffer)
    }
    reader.onerror = () => {
      finish()
      reject(new Error('Não foi possível ler este arquivo.'))
    }
    reader.onabort = finish
    signal?.addEventListener('abort', cancel, {once: true})
    reader.readAsArrayBuffer(blob)
  })
}

export function decodeAttachmentText(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer)
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(buffer)
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(buffer)
  try {
    return new TextDecoder('utf-8', {fatal: true}).decode(buffer)
  } catch {
    return new TextDecoder('windows-1252').decode(buffer)
  }
}

// Inspect raster signatures before assigning an inline MIME type. Never embed active HTML or SVG.
function rasterMime(bytes: Uint8Array) {
  const ascii = (start: number, count: number) =>
    String.fromCharCode(...Array.from(bytes.slice(start, start + count)))
  if ([137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => bytes[index] === value))
    return 'image/png'
  if (bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) return 'image/jpeg'
  if (['GIF87a', 'GIF89a'].includes(ascii(0, 6))) return 'image/gif'
  if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') return 'image/webp'
  if (ascii(0, 2) === 'BM') return 'image/bmp'
  if (bytes[0] === 0 && bytes[1] === 0 && bytes[2] === 1 && bytes[3] === 0) return 'image/x-icon'
  if (ascii(4, 4) === 'ftyp' && /avif|avis/.test(ascii(8, 24))) return 'image/avif'
  return ''
}

export async function prepareAttachmentPreview(
  blob: Blob,
  file: AttachmentMetadata,
  signal?: AbortSignal
) {
  const kind = getAttachmentPreviewKind(file)
  if (kind === 'unsupported') return {kind, blob}
  const head = new Uint8Array(await readAttachmentBuffer(blob.slice(0, 1024), signal))
  let mime = file.mimeType?.toLowerCase().split(';')[0] || ''
  if (kind === 'image') {
    mime = rasterMime(head)
    if (!mime) throw new Error('O conteúdo do arquivo não pôde ser reconhecido como imagem.')
  } else if (kind === 'pdf') {
    if (!String.fromCharCode(...Array.from(head)).includes('%PDF-'))
      throw new Error('O conteúdo do arquivo não pôde ser reconhecido como PDF.')
    mime = 'application/pdf'
  } else if (kind === 'video') {
    mime = videoTypes[extension(file.name)] || mime
  } else if (kind === 'audio') {
    mime = audioTypes[extension(file.name)] || mime
  }
  return {kind, blob: new Blob([blob], {type: mime || 'application/octet-stream'})}
}
